import { type ReactNode, useState } from 'react';
import { Icon } from '../icons';
import { useUiT } from '../i18n';
import { registerPrimitiveMessages, statesDe, statesEn } from '../i18n/primitives';
import { Button } from './Button';
import { copyText } from './CopyField';
import { cx } from './cx';
import '../styles/primitives/states.css';

registerPrimitiveMessages(statesEn, statesDe);

/** What the kit can read from any error (ApiError of the api-client, fetch errors, Error). */
export function describeError(error: unknown): {
  status?: number;
  code?: string;
  message?: string;
  requestId?: string;
  offline: boolean;
} {
  const e = (error ?? {}) as Record<string, unknown>;
  const status = typeof e.status === 'number' ? e.status : undefined;
  const code = typeof e.code === 'string' ? e.code : undefined;
  const message =
    error instanceof Error ? error.message : typeof e.message === 'string' ? e.message : undefined;
  const requestId =
    typeof e.requestId === 'string'
      ? e.requestId
      : typeof e.request_id === 'string'
        ? e.request_id
        : undefined;
  const offline =
    (typeof navigator !== 'undefined' && navigator.onLine === false) ||
    status === 0 ||
    (error instanceof TypeError && /fetch|network/i.test(error.message));
  return { status, code, message, requestId, offline };
}

export type ErrorStateProps = {
  error?: unknown;
  title?: ReactNode;
  description?: ReactNode;
  onRetry?: () => void;
  retrying?: boolean;
  /** Smaller, for a card or a panel. */
  compact?: boolean;
  /** Extra actions next to "Try again". */
  actions?: ReactNode;
  className?: string;
};

/**
 * One error state (interaction-systems F18): what happened in plain words, Try again, and Copy
 * details (status, code, message, reference) for a support request. Offline gets its own message.
 */
export function ErrorState({
  error,
  title,
  description,
  onRetry,
  retrying,
  compact,
  actions,
  className,
}: ErrorStateProps) {
  const t = useUiT();
  const info = describeError(error);
  const [copied, setCopied] = useState(false);
  const details = [
    info.status !== undefined ? `HTTP ${info.status}` : null,
    info.code,
    info.message,
    info.requestId ? `request ${info.requestId}` : null,
    typeof location !== 'undefined' ? location.pathname : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <div className={cx('state', 'state-error', compact && 'compact', className)} role="alert">
      <span className="state-icon err" aria-hidden="true">
        <Icon name={info.offline ? 'globe' : 'alert'} size={compact ? 16 : 20} />
      </span>
      <div className="state-title">
        {title ?? (info.offline ? t('ui.error.offlineTitle') : t('ui.error.title'))}
      </div>
      <div className="state-text">
        {description ?? (info.offline ? t('ui.error.offlineBody') : t('ui.error.body'))}
        {info.requestId ? (
          <span className="state-ref mono"> {t('ui.error.reference', { id: info.requestId })}</span>
        ) : null}
      </div>
      <div className="state-actions">
        {onRetry ? (
          <Button size="sm" variant="primary" icon="refresh" loading={retrying} onClick={onRetry}>
            {t('ui.error.retry')}
          </Button>
        ) : null}
        {details && error !== undefined ? (
          <Button
            size="sm"
            variant="ghost"
            icon={copied ? 'check' : 'copy'}
            onClick={async () => {
              if (await copyText(details)) setCopied(true);
            }}
          >
            {copied ? t('ui.error.copied') : t('ui.error.copy')}
          </Button>
        ) : null}
        {actions}
      </div>
    </div>
  );
}
